/**
 * Service-worker registration helper — Phase 6 / M5.3.
 *
 * Called once from App.svelte after the session is ready. Handles the
 * three browser fallback paths: no SW support (server-side / very old
 * browser), insecure origin (file://, plain http on a non-localhost
 * host), and registration error (network 404, syntax error in sw.js).
 *
 * Idempotent — calling register('/sw.js') twice resolves to the same
 * registration object; the browser dedupes by script URL.
 *
 * Returns the registration on success, or null when push isn't
 * available. Caller checks the return value before attempting to
 * subscribe.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  // Secure-origin gate. Browsers reject SW registration on
  // http://<non-localhost> — defensive: even if the page somehow loaded
  // there, don't spew a console error on every mount.
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'http:' &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    // `ready` resolves once the SW is active (not just registered) —
    // pushManager.subscribe needs the active state.
    await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}
